// Environment-based configuration utility
export interface AppConfig {
  primaryColor: string;
  secondaryColor: string;
  defaultTheme: 'light' | 'dark';
  defaultLanguage: string;
  apiBaseUrl: string;
  mockApiEnabled: boolean;
  debugMode: boolean;
  debugPayloads: boolean;
}

// Color schemes mapping
export const colorSchemes = {
  sky: {
    primary: 'sky',
    secondary: 'emerald',
    primaryShades: {
      50: 'sky-50',
      100: 'sky-100',
      200: 'sky-200',
      300: 'sky-300',
      400: 'sky-400',
      500: 'sky-500',
      600: 'sky-600',
      700: 'sky-700',
      800: 'sky-800',
      900: 'sky-900',
      950: 'sky-950',
    },
    secondaryShades: {
      50: 'emerald-50',
      100: 'emerald-100',
      200: 'emerald-200',
      300: 'emerald-300',
      400: 'emerald-400',
      500: 'emerald-500',
      600: 'emerald-600',
      700: 'emerald-700',
      800: 'emerald-800',
      900: 'emerald-900',
      950: 'emerald-950',
    }
  },
  blue: {
    primary: 'blue',
    secondary: 'indigo',
    primaryShades: {
      50: 'blue-50',
      100: 'blue-100',
      200: 'blue-200',
      300: 'blue-300',
      400: 'blue-400',
      500: 'blue-500',
      600: 'blue-600',
      700: 'blue-700',
      800: 'blue-800',
      900: 'blue-900',
      950: 'blue-950',
    },
    secondaryShades: {
      50: 'indigo-50',
      100: 'indigo-100',
      200: 'indigo-200',
      300: 'indigo-300',
      400: 'indigo-400',
      500: 'indigo-500',
      600: 'indigo-600',
      700: 'indigo-700',
      800: 'indigo-800',
      900: 'indigo-900',
      950: 'indigo-950',
    }
  },
  green: {
    primary: 'green',
    secondary: 'teal',
    primaryShades: {
      50: 'green-50',
      100: 'green-100',
      200: 'green-200',
      300: 'green-300',
      400: 'green-400',
      500: 'green-500',
      600: 'green-600',
      700: 'green-700',
      800: 'green-800',
      900: 'green-900',
      950: 'green-950',
    },
    secondaryShades: {
      50: 'teal-50',
      100: 'teal-100',
      200: 'teal-200',
      300: 'teal-300',
      400: 'teal-400',
      500: 'teal-500',
      600: 'teal-600',
      700: 'teal-700',
      800: 'teal-800',
      900: 'teal-900',
      950: 'teal-950',
    }
  },
  purple: {
    primary: 'purple',
    secondary: 'violet',
    primaryShades: {
      50: 'purple-50',
      100: 'purple-100',
      200: 'purple-200',
      300: 'purple-300',
      400: 'purple-400',
      500: 'purple-500',
      600: 'purple-600',
      700: 'purple-700',
      800: 'purple-800',
      900: 'purple-900',
      950: 'purple-950',
    },
    secondaryShades: {
      50: 'violet-50',
      100: 'violet-100',
      200: 'violet-200',
      300: 'violet-300',
      400: 'violet-400',
      500: 'violet-500',
      600: 'violet-600',
      700: 'violet-700',
      800: 'violet-800',
      900: 'violet-900',
      950: 'violet-950',
    }
  },
  orange: {
    primary: 'orange',
    secondary: 'amber',
    primaryShades: {
      50: 'orange-50',
      100: 'orange-100',
      200: 'orange-200',
      300: 'orange-300',
      400: 'orange-400',
      500: 'orange-500',
      600: 'orange-600',
      700: 'orange-700',
      800: 'orange-800',
      900: 'orange-900',
      950: 'orange-950',
    },
    secondaryShades: {
      50: 'amber-50',
      100: 'amber-100',
      200: 'amber-200',
      300: 'amber-300',
      400: 'amber-400',
      500: 'amber-500',
      600: 'amber-600',
      700: 'amber-700',
      800: 'amber-800',
      900: 'amber-900',
      950: 'amber-950',
    }
  }
};

// Get configuration from environment variables
export const getAppConfig = (): AppConfig => {
  const primaryColor = import.meta.env.VITE_PRIMARY_COLOR || 'sky';
  const secondaryColor = import.meta.env.VITE_SECONDARY_COLOR || 'emerald';
  
  return {
    primaryColor,
    secondaryColor,
    defaultTheme: (import.meta.env.VITE_DEFAULT_THEME as 'light' | 'dark') || 'dark',
    defaultLanguage: import.meta.env.VITE_DEFAULT_LANGUAGE || 'en',
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
    mockApiEnabled: import.meta.env.VITE_MOCK_API_ENABLED === 'true',
    debugMode: import.meta.env.VITE_DEBUG_MODE === 'true',
    debugPayloads: import.meta.env.VITE_DEBUG_PAYLOADS === 'true',
  };
};

// Get current color scheme
export const getColorScheme = () => {
  const config = getAppConfig();
  return colorSchemes[config.primaryColor as keyof typeof colorSchemes] || colorSchemes.sky;
};

// Generate CSS classes for colors
export const getColorClasses = (shade: keyof typeof colorSchemes.sky.primaryShades, isDark: boolean = false) => {
  const scheme = getColorScheme();
  const colorClass = scheme.primaryShades[shade];
  
  if (isDark) {
    return {
      background: `bg-${colorClass}`,
      text: `text-${colorClass}`,
      border: `border-${colorClass}`,
      hover: `hover:bg-${colorClass}`,
      focus: `focus:ring-${colorClass}`,
    };
  } else {
    return {
      background: `bg-${colorClass}`,
      text: `text-${colorClass}`,
      border: `border-${colorClass}`,
      hover: `hover:bg-${colorClass}`,
      focus: `focus:ring-${colorClass}`,
    };
  }
};

// Generate notification-style classes
export const getNotificationClasses = (type: 'success' | 'error' | 'warning' | 'info', isDark: boolean = false) => {
  const scheme = getColorScheme();
  
  switch (type) {
    case 'success':
      return isDark 
        ? `bg-${scheme.secondaryShades[950]}/40 border-${scheme.secondaryShades[800]} text-${scheme.secondaryShades[300]}`
        : `bg-${scheme.secondaryShades[50]} border-${scheme.secondaryShades[200]} text-${scheme.secondaryShades[800]}`;
    case 'error':
      return isDark 
        ? 'bg-red-950/40 border-red-800 text-red-300'
        : 'bg-red-50 border-red-200 text-red-800';
    case 'warning':
      return isDark 
        ? 'bg-amber-950/40 border-amber-800 text-amber-300'
        : 'bg-amber-100 border-amber-300 text-amber-900';
    case 'info':
    default:
      return isDark 
        ? `bg-${scheme.primaryShades[950]}/40 border-${scheme.primaryShades[800]} text-${scheme.primaryShades[300]}`
        : `bg-${scheme.primaryShades[50]} border-${scheme.primaryShades[200]} text-${scheme.primaryShades[800]}`;
  }
};

// Generate step classes
export const getStepClasses = (state: 'active' | 'completed' | 'error' | 'default', isDark: boolean = false) => {
  const scheme = getColorScheme();
  
  switch (state) {
    case 'active':
      return isDark 
        ? `bg-${scheme.primaryShades[950]}/40 border-${scheme.primaryShades[800]} text-${scheme.primaryShades[300]}`
        : `bg-${scheme.primaryShades[50]} border-${scheme.primaryShades[200]} text-${scheme.primaryShades[800]}`;
    case 'completed':
      return isDark 
        ? `bg-${scheme.secondaryShades[950]}/40 border-${scheme.secondaryShades[800]} text-${scheme.secondaryShades[300]}`
        : `bg-${scheme.secondaryShades[50]} border-${scheme.secondaryShades[200]} text-${scheme.secondaryShades[800]}`;
    case 'error':
      return isDark 
        ? 'bg-red-950/40 border-red-800 text-red-300'
        : 'bg-red-50 border-red-200 text-red-800';
    case 'default':
    default:
      return isDark 
        ? 'bg-neutral-800/60 border-neutral-700 hover:bg-neutral-700/60 text-neutral-300'
        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700';
  }
};
